import React from "react";
import {
  Text,
  tokens,
  Button,
  Tooltip,
  mergeClasses,
  Spinner,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Message, Attachment, EditProposal } from "../../../../../contexts/SettingsContext";
import { ArrowClockwiseRegular } from "@fluentui/react-icons";
import { useMessageRowStyles } from "./style";
import MarkdownContent from "./MarkdownContent";
import EditProposalsCard from "./EditProposalsCard";

const attachmentLabel = (a: string | Attachment): string => {
  if (typeof a === "string") return a;
  return `${a.range} (${a.headers.length} columns)`;
};

const MessageRow: React.FC<{
  msg: Message;
  styles: any;
  onRetry?: (messageId: string) => void;
  isRetrying?: boolean;
  onProposalsChange?: (messageId: string, proposals: EditProposal[]) => void;
}> = ({ msg, styles, onRetry, isRetrying, onProposalsChange }) => {
  const { t } = useTranslation();
  const localStyles = useMessageRowStyles();
  const isUser = msg.role === "user";

  if (!isUser) {
    return (
      <div
        className={mergeClasses(
          styles.messageRow,
          styles.assistantRow,
          localStyles.messageContainer
        )}
      >
        <div className={styles.assistantMessage}>
          <MarkdownContent content={msg.content} />

          {msg.proposals && msg.proposals.length > 0 && (
            <EditProposalsCard
              messageId={msg.id}
              proposals={msg.proposals}
              onProposalsChange={onProposalsChange}
            />
          )}

          {/* Show loading indicator for assistant message when retrying */}
          {isRetrying && msg.status === "sending" && (
            <div className={mergeClasses(localStyles.statusIndicator, localStyles.sendingText)}>
              <Spinner size="extra-tiny" />
              {t("chat.thinking")}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={mergeClasses(styles.messageRow, styles.userRow, localStyles.messageContainer)}>
      <div className={styles.userMessage}>
        {msg.content}

        {msg.attachments && msg.attachments.length > 0 && (
          <div className={[styles.attachmentBlock, styles.userAttachmentBlock].join(" ")}>
            <Text size={200} weight="semibold">
              {t("chat.attachedRows")}
            </Text>

            {msg.attachments.map((a, i) => (
              <div
                key={i}
                style={{
                  fontFamily: '"Cascadia Mono", "Courier New", monospace',
                  color: tokens.colorNeutralForegroundOnBrand,
                }}
              >
                {attachmentLabel(a)}
              </div>
            ))}
          </div>
        )}

        {/* Show status indicator */}
        {msg.status === "sending" && (
          <div className={mergeClasses(localStyles.statusIndicator, localStyles.sendingText)}>
            <Spinner size="extra-tiny" />
            {t("chat.sending")}
          </div>
        )}

        {/* Show error and retry button */}
        {msg.status === "error" && isUser && (
          <div className={localStyles.errorMessage}>
            {t("chat.errorSending")}

            <Tooltip content={t("chat.retryTooltip")} relationship="label">
              <Button
                icon={<ArrowClockwiseRegular />}
                appearance="subtle"
                size="small"
                onClick={() => onRetry(msg.id!)}
                className={localStyles.retryIconButton}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageRow;
